<script setup lang="ts">
import { computed, nextTick, watch } from "vue";
import { useRoute } from "vue-router";
import BrandMark from "./components/BrandMark.vue";
import SiteFooter from "./components/SiteFooter.vue";
import SiteHeader from "./components/SiteHeader.vue";
import ContactPage from "./pages/ContactPage.vue";
import { initContactForm } from "./ui/contact-form";

type Block = { title: string; body: string };
const route = useRoute();
const page = computed(() => route.path.slice(1) || "home");
const content: Record<
  string,
  { label: string; title: string; intro: string; blocks: Block[] }
> = {
  services: {
    label: "Services",
    title: "Websites built to do one job well.",
    intro:
      "We build sites for businesses that need customers to understand them fast, then act: book a table, request a quote, buy a product.",
    blocks: [
      {
        title: "How pricing works",
        body: "Every project starts with a fixed-price proposal, not an hourly estimate. You know the total cost before we write a line of code.",
      },
    ],
  },
  process: {
    label: "Process",
    title: "A clear route from first call to launch.",
    intro:
      "Good websites do not appear out of nowhere. We work in clear stages, with a real site to review at every one.",
    blocks: [
      {
        title: "01. Plan",
        body: "We listen to what your business needs, then prepare a working homepage preview and a fixed proposal.",
      },
      {
        title: "02. Build",
        body: "You review real work, request changes, and we refine the details until the site feels right.",
      },
      {
        title: "03. Prepare",
        body: "We finish copy, optimise images, test forms and links, and check the site on slow connections and small screens.",
      },
    ],
  },
  privacy: {
    label: "Privacy",
    title: "What we collect, and for how long.",
    intro:
      "The contact form collects only what it asks for. We use it only to reply to you, and delete it once it is no longer useful.",
    blocks: [
      {
        title: "What the contact form collects",
        body: "We receive your name, email address, company name if you give one, and your message.",
      },
      {
        title: "How we use it",
        body: "Only to read your message and reply to it. We do not add you to a marketing list or sell your details.",
      },
      {
        title: "How long we keep it",
        body: "We keep messages and any following email for up to 24 months, then delete them.",
      },
    ],
  },
};
const services = [
  [
    "From scratch",
    "New website",
    "A full site built around what you sell and how people find you.",
  ],
  [
    "Rebuild",
    "Redesign",
    "Your existing site, rebuilt on faster, more maintainable foundations.",
  ],

  [
    "After launch",
    "Ongoing care",
    "Monthly checks and small content changes, so the site keeps working.",
  ],
];
const stages = [
  [
    "Stage 01 - Plan",
    "We start by listening.",
    "You get a working preview and a written proposal with scope, timeline, and price before anything is built.",
  ],
  [
    "Stage 02 - Build",
    "You steer. We build.",
    "We build in the open, revise your copy and layout feedback, and keep iterating until it is right.",
  ],
  [
    "Stage 03 - Prepare",
    "Nothing ships half-finished.",
    "We proofread copy, compress images, set up search basics, and test every form and link.",
  ],
];
watch(
  () => route.path,
  async () => {
    await nextTick();
    initContactForm();
    window.dispatchEvent(new Event("app:route-change"));
  },
);
</script>

<template>
  <div class="boot" data-boot>
    <div class="boot__inner">
      <BrandMark />
      <p class="boot__label mono-label">Preparing the ascent</p>
      <span class="boot__track" aria-hidden="true"
        ><span class="boot__fill"
      /></span>
    </div>
  </div>
  <div class="scene-fallback" aria-hidden="true" />
  <div class="page-scrim" aria-hidden="true" />
  <a class="skip-link" href="#main">Skip to content</a><SiteHeader />
  <main id="main">
    <template v-if="page === 'home'"
      ><section class="hero shell">
        <div class="hero__inner copy-scrim">
          <p class="hero__eyebrow mono-label">Base camp</p>
          <h1>Your website, built like a climb, not a guess.</h1>
          <p class="hero__lede">
            North Plan Digital designs and builds websites for small and
            independent businesses. We work in three stages: plan, build, prepare.
          </p>
          <p class="scroll-cue mono-label">Scroll to begin the climb</p>
        </div>
        <div class="scene-mount" data-scene-mount aria-hidden="true" />
      </section>
      <section
        v-for="(stage, index) in stages"
        :key="stage[0]"
        class="waypoint shell"
        :class="index === 2 ? 'waypoint--right' : 'waypoint--left'"
      >
        <div class="waypoint__inner copy-scrim">
          <p class="waypoint__label mono-label">{{ stage[0] }}</p>
          <h2>{{ stage[1] }}</h2>
          <p>{{ stage[2] }}</p>
        </div>
      </section>
      <section class="summit shell">
        <div class="summit__inner copy-scrim">
          <p class="summit__label mono-label">Summit</p>
          <h2>Launch.</h2>
          <p>
            We switch the site live, check every page, form, and redirect, and
            hand you the keys.
          </p>
          <RouterLink class="btn btn--cta" to="/contact"
            >Ascend your business</RouterLink
          >
        </div>
      </section></template
    >
    <ContactPage v-else-if="page === 'contact'" />
    <section v-else-if="!content[page]" class="page-hero shell">
      <div class="page-hero__inner copy-scrim">
        <p class="page-hero__label mono-label">404 - Off the trail</p>
        <h1>That page doesn't exist.</h1>
        <p>The link's broken or the page moved.</p>
        <RouterLink class="btn btn--cta" to="/">Back to the homepage</RouterLink>
      </div>
    </section>
    <template v-else
      ><section class="page-hero shell">
        <div class="page-hero__inner copy-scrim">
          <p class="page-hero__label mono-label">{{ content[page].label }}</p>
          <h1>{{ content[page].title }}</h1>
          <p>{{ content[page].intro }}</p>
        </div>
      </section>
      <section v-if="page === 'services'" class="block shell">
        <div class="card-grid">
          <div v-for="service in services" :key="service[1]" class="card">
            <p class="card__label mono-label">{{ service[0] }}</p>
            <h3>{{ service[1] }}</h3>
            <p>{{ service[2] }}</p>
          </div>
        </div>
      </section>
      <section
        v-for="block in content[page].blocks"
        :key="block.title"
        class="block shell"
      >
        <div class="block__inner copy-scrim">
          <h2>{{ block.title }}</h2>
          <p>{{ block.body }}</p>
        </div>
      </section></template
    >
  </main>
  <SiteFooter />
</template>
